<template>
  <div class="rent-planner-create">
    <Loading v-if="loading" type="line" :center="false" />

    <div v-else>
      <div v-if="!selectedFunnel" class="rent-planner-create-funnels">
        <div v-if="funnels.length === 0" class="rent-planner-create-empty">
          <i class="fa-solid fa-chart-kanban"></i>
          <span>Nenhum quadro disponível.</span>
          <span class="rent-planner-create-empty-sub">Acesse o Planner para criar o primeiro quadro.</span>
          <Button
            _key="btnGoToCreateFunnel"
            type="primary"
            title="Ir para o Planner e criar quadro"
            classIcon="fa-solid fa-chart-kanban"
            size="medium"
            :loading="isLoading('btnGoToCreateFunnel')"
            :clicked="goToPlannerToCreateFunnel"
          />
        </div>

        <div
          v-for="funnel in funnels"
          :key="funnel.id"
          class="rent-planner-create-funnel-item"
          @click="selectFunnel(funnel)"
        >
          <div class="rent-planner-create-funnel-icon">
            <i class="fa-solid fa-chart-kanban"></i>
          </div>
          <div class="rent-planner-create-funnel-info">
            <span class="rent-planner-create-funnel-name">{{ funnel.name }}</span>
            <span v-if="funnel.description" class="rent-planner-create-funnel-desc">{{ funnel.description }}</span>
          </div>
          <i class="fa-solid fa-chevron-right rent-planner-create-funnel-arrow"></i>
        </div>
      </div>

      <div v-else-if="created" class="rent-planner-create-success">
        <div class="rent-planner-create-success-icon">
          <i class="fa-solid fa-circle-check"></i>
        </div>
        <span class="rent-planner-create-success-title">Cartão criado com sucesso!</span>
        <span class="rent-planner-create-success-sub">Deseja ir para o quadro <strong>{{ selectedFunnel.name }}</strong>?</span>
        <div class="rent-planner-create-success-actions">
          <Button
            _key="btnGoToPlanner"
            type="primary"
            title="Ir para o Planner"
            classIcon="fa-solid fa-chart-kanban"
            size="medium"
            :loading="isLoading('btnGoToPlanner')"
            :clicked="goToPlanner"
          />
          <Button
            _key="btnStayHere"
            type="edit"
            title="Agora não"
            size="medium"
            :loading="isLoading('btnStayHere')"
            :clicked="closeModal"
          />
        </div>
      </div>

      <div v-else>
        <div class="rent-planner-create-back" @click="backToFunnels">
          <i class="fa-solid fa-arrow-left"></i>
          <span>{{ selectedFunnel.name }}</span>
        </div>

        <div class="rent-planner-create-section-label">Selecione a etapa</div>

        <Loading v-if="loadingSteps" type="line" :center="false" />

        <div v-else>
          <div v-if="steps.length === 0" class="rent-planner-create-empty">
            <span>Nenhuma etapa encontrada neste quadro.</span>
          </div>

          <div class="rent-planner-create-steps" v-else>
            <div
              v-for="step in steps"
              :key="step.id"
              class="rent-planner-create-step-pill"
              :class="{ 'rent-planner-create-step-pill-active': selectedStep && selectedStep.id === step.id }"
              @click="selectedStep = step"
            >
              <span class="rent-planner-create-step-dot" :style="{ background: step.color }"></span>
              {{ step.name }}
            </div>
          </div>

          <div class="rent-planner-create-footer">
            <Button
              _key="btnCreatePlannerCard"
              type="success"
              title="Criar"
              classIcon="fa-solid fa-plus"
              size="medium"
              :disabled="!selectedStep"
              :loading="isLoading('btnCreatePlannerCard')"
              :clicked="create"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/ui/forms/Button";
import Loading from "@/ui/shared/Loading.vue";

import CardService from "../../services/card.service";
import FunnelService from "../../services/funnel.service";
import StepService from "../../services/step.service";

import { mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "RentPlannerCreate",
  components: { Button, Loading },
  props: {
    rentId: {
      type: String,
      required: true,
    },
    customer: {
      type: Object,
      default: () => ({ id: "", content: "" }),
    },
  },
  data() {
    return {
      loading: false,
      loadingSteps: false,
      funnels: [],
      steps: [],
      selectedFunnel: null,
      selectedStep: null,
      created: false,
    };
  },
  mounted() {
    this.loadFunnels();
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapGetters("generic", ["isLoading"]),
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "addLoading", "removeLoading", "hideModal"]),
    async loadFunnels() {
      this.loading = true;
      const response = await FunnelService.getAll(this.userLogged.user.id);
      this.funnels = response.data.content.data;
      this.loading = false;
    },
    async selectFunnel(funnel) {
      this.selectedFunnel = funnel;
      this.selectedStep = null;
      this.loadingSteps = true;
      const response = await StepService.getAll(funnel.id);
      this.steps = response.data.content.data;
      this.loadingSteps = false;
    },
    backToFunnels() {
      this.selectedFunnel = null;
      this.selectedStep = null;
      this.steps = [];
    },
    async create() {
      this.addLoading("btnCreatePlannerCard");
      try {
        const cardResponse = await CardService.create({
          frameId: this.selectedFunnel.id,
          stepId: this.selectedStep.id,
          userId: this.userLogged.user.id,
          customer: this.customer,
          description: "",
          tags: [],
        });
        const cardId = cardResponse.data.content.id;
        await CardService.addRentLinked(cardId, this.rentId);
        this.addEvent({ name: "plannerCardCreated" });
        this.created = true;
      } finally {
        this.removeLoading(["btnCreatePlannerCard"]);
      }
    },
    goToPlanner() {
      this.addLoading("btnGoToPlanner");
      this.removeLoading(["btnGoToPlanner"]);
      this.hideModal();
      this.$router.push({
        path: `/planner/kanban/${this.selectedFunnel.id}/${encodeURIComponent(this.selectedFunnel.name)}`,
      });
    },
    goToPlannerToCreateFunnel() {
      this.addLoading("btnGoToCreateFunnel");
      this.removeLoading(["btnGoToCreateFunnel"]);
      this.hideModal();
      this.$router.push({ name: "funnelList" });
    },
    closeModal() {
      this.addLoading("btnStayHere");
      this.removeLoading(["btnStayHere"]);
      this.hideModal();
    },
  },
};
</script>

<style scoped>
.rent-planner-create {
  padding: 4px 0;
}

.rent-planner-create-funnels {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rent-planner-create-funnel-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  cursor: pointer;
  background: var(--bg-base);
  transition: background 0.15s, border-color 0.15s;
}

.rent-planner-create-funnel-item:hover {
  background: var(--bg-hover);
  border-color: var(--color-brand-light);
}

.rent-planner-create-funnel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(87, 118, 150, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand);
  font-size: 15px;
  flex-shrink: 0;
}

.rent-planner-create-funnel-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.rent-planner-create-funnel-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rent-planner-create-funnel-desc {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}

.rent-planner-create-funnel-arrow {
  font-size: 12px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.rent-planner-create-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-brand);
  cursor: pointer;
  margin-bottom: 16px;
  transition: color 0.15s;
}

.rent-planner-create-back:hover {
  color: var(--color-brand-hover);
}

.rent-planner-create-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.rent-planner-create-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.rent-planner-create-step-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 16px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-base);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.rent-planner-create-step-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  flex-shrink: 0;
  display: inline-block;
}

.rent-planner-create-step-pill:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.rent-planner-create-step-pill-active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: var(--text-inverse);
}

.rent-planner-create-step-pill-active:hover {
  background: var(--color-brand-hover);
  border-color: var(--color-brand-hover);
  color: var(--text-inverse);
}

.rent-planner-create-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
  border-top: 1px solid var(--border-subtle);
  margin-top: 8px;
}

.rent-planner-create-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 0;
  color: var(--text-muted);
  font-size: 14px;
}

.rent-planner-create-empty i {
  font-size: 32px;
  color: var(--color-brand-light);
}

.rent-planner-create-empty-sub {
  font-size: 13px;
  color: var(--text-secondary);
  font-style: italic;
  margin-top: -4px;
  margin-bottom: 4px;
}

.rent-planner-create-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 16px;
  text-align: center;
}

.rent-planner-create-success-icon {
  font-size: 40px;
  color: var(--color-success);
}

.rent-planner-create-success-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.rent-planner-create-success-sub {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.rent-planner-create-success-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
</style>

